import {
  composeRefs,
  isComponentOfType,
  refContainsTarget,
  useClickedOutside,
  useStableCallback
} from "@prismicio/editor-support/React"
import {
  PopoverAnchor,
  PopoverContent,
  PopoverPortal,
  Root
} from "@radix-ui/react-popover"
import { clsx } from "clsx"
import {
  Children,
  createContext,
  forwardRef,
  type ReactNode,
  type RefObject,
  useContext,
  useEffect,
  useRef
} from "react"

import type { SX } from "../../theme"
import { FormSearchInput } from "../Form"
import { Icon } from "../Icon"
import { SearchInput } from "../SearchInput"
import styles from "./ComboBox.module.css"
import { PICK_EVENT } from "./ComboBoxConstants"
import {
  getSelectedItem,
  useInternalSelection,
  useScrollIntoView,
  useSelectFirst
} from "./useComboBox"

type Variant = "attached" | "detached"
interface Context {
  /**
   * Internal state controlling whether the "detached" Content is displayed or not
   */
  listIsOpen: boolean
  /**
   * When attached, listIsOpen is locked to true
   */
  attached: boolean
  /**
   * Sets internal state controlling whether the "detached" Content is displayed or not
   */
  setListIsOpen: (value: boolean) => void
  contentInnerRef: RefObject<HTMLDivElement>
  contentRef: RefObject<HTMLDivElement>
  detachedInputContainerRef: RefObject<HTMLDivElement>
  inputInnerRef: RefObject<HTMLInputElement>
  /**
   * Currently internally "selected" item value
   */
  selectedValue: string | undefined
  /**
   * Sets internally selected item state
   */
  onChangeInternalSelection: (value: string | undefined) => void
  /**
   * Dispatches a new PICK event
   * Runs the onCheckedChange of a ComboBoxItem when it is externally "selected" or picked
   */
  onItemPicked: () => void
}

const ComboBoxContext = createContext<Context | null>(null)
function useComboBox() {
  const context = useContext(ComboBoxContext)
  if (!context) {
    throw new Error("useComboBox must be used inside a ComboboxProvider")
  }

  return context
}

export interface ComboBoxProps {
  children: ReactNode
  variant?: Variant
  sx?: SX
}

export const ComboBox = forwardRef<HTMLDivElement, ComboBoxProps>(
  function ComboBox(props, ref) {
    const { children, variant = "detached", sx } = props

    const contentRef = useRef<HTMLDivElement>(null)
    const contentInnerRef = useRef<HTMLDivElement>(null)
    const inputInnerRef = useRef<HTMLInputElement>(null)
    const detachedInputContainerRef = useRef<HTMLDivElement>(null)
    const comboBoxInnerRef = useRef<HTMLDivElement>(null)

    const {
      next,
      prev,
      selectedValue,
      listIsOpen: listIsOpenLocal,
      setListIsOpen,
      onChangeInternalSelection,
      onItemPicked,
      cleanupInternalState
    } = useInternalSelection({
      contentInnerRef,
      inputInnerRef
    })

    useClickedOutside([comboBoxInnerRef, contentRef], cleanupInternalState)

    useScrollIntoView({ contentInnerRef })

    const attached = variant === "attached"
    const listIsOpen = attached ? true : listIsOpenLocal

    useSelectFirst({
      contentInnerRef,
      listIsOpen,
      onChangeInternalSelection
    })

    return (
      <div
        ref={composeRefs(comboBoxInnerRef, ref)}
        role="menu"
        tabIndex={-1}
        style={sx}
        className={clsx(styles.combobox, attached && styles.attached)}
        onKeyDown={(event) => {
          if (!event.defaultPrevented) {
            switch (event.key) {
              case "ArrowDown": {
                next(event)
                break
              }
              case "ArrowUp": {
                prev(event)
                break
              }
              case "Enter": {
                onItemPicked()
                cleanupInternalState()
                break
              }
              case "Escape": {
                cleanupInternalState()
                break
              }
              case "Tab": {
                cleanupInternalState()
              }
            }
          }
        }}
      >
        <ComboBoxContext.Provider
          value={{
            inputInnerRef,
            contentRef,
            contentInnerRef,
            selectedValue,
            setListIsOpen,
            listIsOpen,
            onChangeInternalSelection,
            onItemPicked,
            attached,
            detachedInputContainerRef
          }}
        >
          {children}
        </ComboBoxContext.Provider>
      </div>
    )
  }
)

interface ComboBoxContentProps {
  children: ReactNode
  /**
   * Aligns "detached" content above the input or below
   * @variant - detached
   */
  side?: "top" | "bottom"
  /**
   * Adds padding to the top and bottom of the content
   */
  paddingBlock?: 0 | 2 | 4
}

export const ComboBoxContent = forwardRef<HTMLDivElement, ComboBoxContentProps>(
  function ComboBoxContent(
    { children, side = "bottom", paddingBlock = 0 },
    ref
  ) {
    const {
      contentRef,
      contentInnerRef,
      listIsOpen,
      onItemPicked,
      attached,
      detachedInputContainerRef
    } = useComboBox()

    const childNodes = Children.toArray(children)

    const actions = childNodes.filter((child) =>
      isComponentOfType(child, ComboboxAction)
    )

    const rest = childNodes.filter(
      (child) => !isComponentOfType(child, ComboboxAction)
    )

    const content = (
      <div
        ref={contentRef}
        className={clsx(
          styles.comboboxContent,
          attached && styles.attached,
          styles[`side-${side}`]
        )}
      >
        <div
          ref={composeRefs(ref, contentInnerRef)}
          role="listbox"
          tabIndex={-1}
          className={clsx(
            styles.items,
            paddingBlock && styles[`paddingBlock-${paddingBlock}`]
          )}
          onKeyDown={(event) => event.preventDefault()}
          onClick={(event) => {
            event.preventDefault()
            if (refContainsTarget(contentInnerRef, event.currentTarget)) {
              onItemPicked()
            }
          }}
        >
          {rest}
        </div>
        {actions}
      </div>
    )

    // Locks to true in attached variant
    if (listIsOpen === false) return null

    if (attached === false)
      return (
        <Root open={listIsOpen}>
          <PopoverAnchor virtualRef={detachedInputContainerRef} />
          <PopoverPortal>
            <PopoverContent
              asChild
              align="start"
              side={side}
              avoidCollisions={false}
              onOpenAutoFocus={(event) => event.preventDefault()}
            >
              {content}
            </PopoverContent>
          </PopoverPortal>
        </Root>
      )

    return content
  }
)

type ComboBoxItemProps = {
  value: string
  checked?: boolean
  onCheckedChange?: (checked: boolean) => void
  disabled?: boolean
  children: ReactNode
}

export const ComboBoxItem = forwardRef<HTMLDivElement, ComboBoxItemProps>(
  function ComboBoxItem(props, forwardRef) {
    const { value, checked, onCheckedChange, disabled = false } = props
    const ref = useRef<HTMLDivElement>(null)

    const { selectedValue, onChangeInternalSelection, setListIsOpen } =
      useComboBox()

    const onSelectEvent = useStableCallback(() => {
      onCheckedChange?.(!checked)
      setListIsOpen(false)
    })

    useEffect(() => {
      const element = ref.current
      if (!element || disabled) return
      element.addEventListener(PICK_EVENT, onSelectEvent)
      return () => element.removeEventListener(PICK_EVENT, onSelectEvent)
    }, [disabled, onSelectEvent])

    return (
      <div
        ref={composeRefs(ref, forwardRef)}
        role="option"
        className={styles.comboboxItem}
        combobox-item=""
        tabIndex={-1}
        aria-selected={selectedValue === value}
        aria-disabled={disabled}
        aria-checked={checked}
        data-value={value}
        // Why on mouse move not on mouse enter?
        // Since the list can be scrolled both with the keyboard and the mouse,
        // scrolling with the keyboard triggers onMouseEnter if the mouse sits
        // anywhere within the list.
        onMouseMove={() => onChangeInternalSelection(value)}
      >
        {props.children}
        {checked && (
          <Icon
            name="check"
            size="medium"
            sx={{
              flexShrink: 0,
              marginLeft: "auto",
              marginRight: 8,
              marginBlock: 4
            }}
          />
        )}
      </div>
    )
  }
)

interface ComboBoxInputProps {
  value: string
  onValueChange: (search: string) => void
  placeholder?: string
  /**
   * Auto focuses the input when variant is "attached".
   * Needed when ComboBox is nested within Popovers
   */
  autoFocus?: boolean
}

export const ComboBoxInput = forwardRef<HTMLInputElement, ComboBoxInputProps>(
  function ComboBoxInput(props, forwardedRef) {
    const { onValueChange, value, placeholder, autoFocus } = props
    const {
      contentInnerRef,
      setListIsOpen,
      inputInnerRef,
      listIsOpen,
      attached,
      detachedInputContainerRef
    } = useComboBox()

    function onFocus() {
      setListIsOpen(true)
    }

    const inputProps = {
      ref: composeRefs(forwardedRef, inputInnerRef),
      placeholder: placeholder,
      role: "combobox",
      "aria-expanded": listIsOpen,
      "aria-activedescendant": getSelectedItem(contentInnerRef),
      value: value ?? "",
      onValueChange: (value: string) => onValueChange?.(value),
      onFocus: onFocus,
      endAdornment: false
    }

    if (!attached)
      return (
        <div ref={detachedInputContainerRef}>
          <FormSearchInput {...inputProps} />
        </div>
      )

    return (
      <div className={clsx(styles.input, attached && styles.attached)}>
        {/* eslint-disable-next-line jsx-a11y/no-autofocus */}
        <SearchInput {...inputProps} autoFocus={autoFocus} />
      </div>
    )
  }
)

interface ComboBoxActionProps {
  children: ReactNode
}

export function ComboboxAction(props: ComboBoxActionProps) {
  const { children } = props

  const { attached } = useComboBox()

  return (
    <div className={clsx(styles.comboboxAction, attached && styles.attached)}>
      {children}
    </div>
  )
}
